import { motion } from 'framer-motion';
import { darken, rgba } from 'polished';
import styled from 'styled-components';

import {
  ContentMarginLine,
  gridColorSelector
} from '@/components/v2/GridLines';
import { useHeaderUI } from '@/components/v2/Header/HeaderUIProvider';
import {
  LayoutGridCondensed,
  layoutGridCondensedPadding,
  LayoutSubgrid
} from '@/components/v2/Page/LayoutGrid';
import { styles as headingStyles } from '@/components/v2/UtilityCss/Heading';
import { styles as textStyles } from '@/components/v2/UtilityCss/Text';

import { config } from './config';
import { useAnchorContext } from './Context';

const Sticky = styled(LayoutSubgrid)`
  position: sticky;
  z-index: 1;
  border-bottom: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
  background-color: ${({ theme }) => darken(0.0, theme.palette.fg100)};

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    position: relative;
    top: 0 !important;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
  }
`;

const HatchedPlaceholder = styled(LayoutGridCondensed)`
  grid-row: 2 / -1;

  &::after {
    content: '';
    grid-column: 2 / -2;
    border: 1px solid ${({ theme }) => theme.palette.fg500};
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
    border-bottom: 0;
    margin-top: -1px;
    margin-left: -1px;
    margin-right: -1px;
    min-width: 80px;
  }
`;

const Shadow = styled(LayoutSubgrid)`
  position: absolute;
  inset: 0;
  box-shadow: 0 8px 16px -6px rgba(0 0 0 / 0);
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

const ContentsWrapper = styled(LayoutGridCondensed)`
  grid-row: 2 / -1;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    height: 32px;
  }
`;

const Contents = styled.div`
  grid-column: 2 / -2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: content-box;
  padding: 0 ${layoutGridCondensedPadding};

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

const Nav = styled.ul`
  display: flex;
  gap: 24px;
`;

const NavTitle = styled.span`
  color: ${({ theme }) => theme.palette.fgLoud};
  margin-bottom: -4px;
`;

const Workflow = styled.a`
  border: none;
  color: ${({ theme }) => rgba(theme.palette.fgLoud, 0.56)};
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 14px 0 12px 0;
  border-bottom: 1px solid transparent;
  margin-bottom: -1px;

  &:hover,
  &[data-active='true'] {
    background: transparent;
    color: var(--color);
  }

  &[data-active='true'] {
    border-bottom: 1px solid var(--color);
  }
`;

const VLine = styled.div`
  grid-column: 1 / -1;
  display: grid;
  place-content: center;
  height: 10px;

  &::after {
    content: '';
    height: 10px;
    width: 1px;
    background-color: ${({ theme }) => theme.palette.fg500};
    margin-right: -1px;
  }
`;

export function StickyNav() {
  const { navHeight, gridlineHeight } = useHeaderUI();
  const { anchor } = useAnchorContext();

  return (
    <Sticky style={{ top: navHeight + gridlineHeight }}>
      <Shadow
        as={motion.div}
        initial={{ boxShadow: '0 8px 16px -6px rgba(0 0 0 / 0)' }}
        viewport={{ amount: 'all', margin: '0px 0px -76% 0px' }}
        whileInView={{ boxShadow: '0 6px 12px -2px rgba(0 0 0 / 0.24)' }}
      />
      <LayoutSubgrid>
        <ContentMarginLine />
        <LayoutSubgrid data-content-grid>
          <VLine />
          <LayoutSubgrid data-content-grid>
            <HatchedPlaceholder />
            <ContentsWrapper>
              <Contents
                as={motion.div}
                initial={{ opacity: 0 }}
                viewport={{ amount: 'all', margin: '0px 0px -76% 0px' }}
                whileInView={{ opacity: 1 }}>
                <NavTitle className={headingStyles.h4}>
                  An integrated workspace for analytics
                </NavTitle>
                <Nav>
                  <li>
                    <Workflow
                      className={textStyles.base}
                      data-active={
                        anchor
                          ? config.workflows.notebooks.anchor
                              .toLocaleLowerCase()
                              .includes(anchor.toLocaleLowerCase())
                          : false
                      }
                      href={'#' + config.workflows.notebooks.anchor}
                      style={{
                        '--color': config.workflows.notebooks.color
                      }}>
                      {config.workflows.notebooks.icon}
                      Deep-dive analysis
                    </Workflow>
                  </li>
                  <li>
                    <Workflow
                      className={textStyles.base}
                      data-active={
                        anchor
                          ? config.workflows.data_apps.anchor
                              .toLocaleLowerCase()
                              .includes(anchor.toLocaleLowerCase())
                          : false
                      }
                      href={'#' + config.workflows.data_apps.anchor}
                      style={{
                        '--color': config.workflows.data_apps.color
                      }}>
                      {config.workflows.data_apps.icon}
                      Interactive data apps
                    </Workflow>
                  </li>
                  <li>
                    <Workflow
                      className={textStyles.base}
                      data-active={
                        anchor
                          ? config.workflows.self_serve.anchor
                              .toLocaleLowerCase()
                              .includes(anchor.toLocaleLowerCase())
                          : false
                      }
                      href={'#' + config.workflows.self_serve.anchor}
                      style={{
                        '--color': config.workflows.self_serve.color
                      }}>
                      {config.workflows.self_serve.icon}
                      Self-serve exploration
                    </Workflow>
                  </li>
                </Nav>
              </Contents>
            </ContentsWrapper>
          </LayoutSubgrid>
        </LayoutSubgrid>
        <ContentMarginLine data-right />
      </LayoutSubgrid>
    </Sticky>
  );
}
